import * as THREE from 'three';
import { Spin } from '../animated';
import { CLASSICAL, INDUSTRIAL, MODERN } from '../materials';
import {
  HexPad,
  hipRoof,
  levelEntry,
  type ModelProps,
  Pole,
  pyramid,
  TrainingDummy,
} from '../parts';

const { WOOD, DARK_WOOD, METAL, STRAW, STONE, PLASTER, SOIL, HORSE } = CLASSICAL;
const { BRICK, IRON, SLATE, SANDBAG } = INDUSTRIAL;
const { CONCRETE, DARK_CONCRETE, STEEL, GLASS } = MODERN;

const G = {
  hall: new THREE.BoxGeometry(0.5, 0.2, 0.26),
  hallRoof: new THREE.CylinderGeometry(0.18, 0.18, 0.54, 3).rotateZ(Math.PI / 2),
  upperStorey: new THREE.BoxGeometry(0.44, 0.12, 0.22),
  stoneHallRoof: hipRoof(0.5, 0.28, 0.14, 0.22),
  // L3 keep: a stone gatehouse wing behind the hall's left end, and a
  // crenellated wall along the tile's back edges.
  wing: new THREE.BoxGeometry(0.2, 0.18, 0.2),
  wingRoof: pyramid(0.15, 0.1),
  wall: new THREE.BoxGeometry(1, 0.1, 0.04),
  wallPost: new THREE.BoxGeometry(0.07, 0.14, 0.07),
  // Extra tile: weapon shed.
  weaponShed: new THREE.BoxGeometry(0.14, 0.12, 0.22),
  weaponShedRoof: new THREE.BoxGeometry(0.19, 0.02, 0.27),
  door: new THREE.BoxGeometry(0.08, 0.12, 0.01),
  yard: new THREE.BoxGeometry(0.6, 0.02, 0.3),
  // L2+: the yard widened to the palisade.
  bigYard: new THREE.BoxGeometry(0.84, 0.02, 0.36),
  // Watchtower (wooden at L2, stone at L3).
  towerDeck: new THREE.BoxGeometry(0.17, 0.03, 0.17),
  towerRoof: pyramid(0.14, 0.1),
  stoneTower: new THREE.BoxGeometry(0.16, 0.44, 0.16),
  merlon: new THREE.BoxGeometry(0.045, 0.05, 0.045),
  banner: new THREE.BoxGeometry(0.1, 0.13, 0.012),
  stake: new THREE.CylinderGeometry(0.03, 0.03, 0.2, 5),
  stakeTip: new THREE.ConeGeometry(0.03, 0.05, 5),
  // Archery target: straw boss, plaster ring, team-coloured centre (faces +z).
  targetOuter: new THREE.CylinderGeometry(0.09, 0.09, 0.03, 12).rotateX(Math.PI / 2),
  targetRing: new THREE.CylinderGeometry(0.062, 0.062, 0.034, 12).rotateX(Math.PI / 2),
  targetCentre: new THREE.CylinderGeometry(0.032, 0.032, 0.038, 10).rotateX(Math.PI / 2),
  spearTip: new THREE.ConeGeometry(0.014, 0.05, 4),
  flag: new THREE.BoxGeometry(0.18, 0.11, 0.01),
  shield: new THREE.CylinderGeometry(0.045, 0.045, 0.014, 10).rotateX(Math.PI / 2),
  shieldBoss: new THREE.SphereGeometry(0.014, 6, 4),
  // Extra tile: stable and sparring ring.
  stableWall: new THREE.BoxGeometry(0.66, 0.14, 0.03),
  stableRoof: new THREE.BoxGeometry(0.72, 0.02, 0.2),
  stableDivider: new THREE.BoxGeometry(0.02, 0.12, 0.16),
  stallFloor: new THREE.BoxGeometry(0.64, 0.012, 0.32),
  horseBody: new THREE.BoxGeometry(0.08, 0.08, 0.2),
  horseHead: new THREE.BoxGeometry(0.05, 0.09, 0.09),
  horseLeg: new THREE.CylinderGeometry(0.012, 0.01, 0.1, 5),
  ring: new THREE.TorusGeometry(0.11, 0.022, 4, 12).rotateX(Math.PI / 2),
  // L4 drill hall: a glazed lantern on the ridge and chimney stacks at the ends.
  lantern: new THREE.BoxGeometry(0.2, 0.05, 0.06),
  lanternRoof: new THREE.BoxGeometry(0.22, 0.015, 0.08),
  stack: new THREE.BoxGeometry(0.04, 0.12, 0.04),
  // L4 iron railings.
  railPost: new THREE.BoxGeometry(0.02, 0.14, 0.02),
  rail: new THREE.BoxGeometry(1, 0.012, 0.012),
  // L5 concrete compound: a flat-roofed hall, perimeter wall, bunker and radar mast.
  concreteHall: new THREE.BoxGeometry(0.54, 0.3, 0.28),
  hallStrip: new THREE.BoxGeometry(0.545, 0.022, 0.285),
  flatRoof: new THREE.BoxGeometry(0.56, 0.03, 0.3),
  perimeterWall: new THREE.BoxGeometry(1, 0.13, 0.035),
  bunkerRoof: new THREE.BoxGeometry(0.22, 0.03, 0.22),
  radarBase: new THREE.BoxGeometry(0.12, 0.08, 0.12),
  radarDish: new THREE.SphereGeometry(0.09, 10, 4, 0, Math.PI * 2, 0, Math.PI / 3).rotateX(
    Math.PI / 2
  ),
  radarLight: new THREE.SphereGeometry(0.015, 6, 4),
  // Extra tile: L4 sandbag firing line and magazine, L5 target boards and a floodlight.
  sandbags: new THREE.BoxGeometry(0.6, 0.06, 0.07),
  magazine: new THREE.BoxGeometry(0.3, 0.14, 0.16),
  magazineRoof: new THREE.BoxGeometry(0.34, 0.025, 0.2),
  board: new THREE.BoxGeometry(0.07, 0.1, 0.012),
  floodlight: new THREE.BoxGeometry(0.08, 0.03, 0.03),
};

const DUMMIES = [-0.18, 0, 0.18];
const TOWER_POS: [number, number, number] = [0.4, 0, -0.28];
const TOWER_LEGS = [
  [-0.06, -0.06],
  [0.06, -0.06],
  [-0.06, 0.06],
  [0.06, 0.06],
];
const MERLONS = TOWER_LEGS.map(([x, z]) => [x * 1.05, z * 1.05]);
// The palisade: a U round the yard — down both sides and along the two front
// edges (inset from the hex edge), with a gate gap in the middle.
const PALISADE_RUNS: readonly [[number, number], [number, number]][] = [
  [
    [0.52, -0.12],
    [0.52, 0.28],
  ],
  [
    [0.49, 0.352],
    [0.12, 0.566],
  ],
  [
    [-0.49, 0.352],
    [-0.12, 0.566],
  ],
  [
    [-0.52, 0.28],
    [-0.52, 0.02],
  ],
];
const STAKE_GAP = 0.07;
const STAKES = PALISADE_RUNS.flatMap(([[x0, z0], [x1, z1]]) => {
  const n = Math.round(Math.hypot(x1 - x0, z1 - z0) / STAKE_GAP);
  return Array.from({ length: n + 1 }, (_, i) => ({
    x: x0 + ((x1 - x0) * i) / n,
    z: z0 + ((z1 - z0) * i) / n,
  }));
});
// L2 armoury: a team-roofed shed left of the hall, clear of the L3 wing behind it.
const ARMOURY: [number, number, number] = [-0.44, 0, -0.12];
const SHIELDS = [-0.16, 0, 0.16];
const RACK_SPEARS = [-0.045, 0, 0.045];

function ArcheryTarget({
  accent,
  position,
}: {
  accent: THREE.Material;
  position: [number, number, number];
}) {
  return (
    <group position={position}>
      <Pole length={0.2} position={[-0.05, 0.09, -0.02]} tilt={-0.25} />
      <Pole length={0.2} position={[0.05, 0.09, -0.02]} tilt={0.25} />
      <group position={[0, 0.16, 0]} rotation={[-0.25, 0, 0]}>
        <mesh geometry={G.targetOuter} material={STRAW} />
        <mesh geometry={G.targetRing} material={PLASTER} />
        <mesh geometry={G.targetCentre} material={accent} />
      </group>
    </group>
  );
}

/** A rack of three spears leaning on a crossbar. */
function SpearRack({ position }: { position: [number, number, number] }) {
  return (
    <group position={position}>
      <Pole length={0.14} position={[-0.07, 0.07, 0]} material={DARK_WOOD} />
      <Pole length={0.14} position={[0.07, 0.07, 0]} material={DARK_WOOD} />
      <Pole length={0.16} position={[0, 0.12, 0]} tilt={Math.PI / 2} material={DARK_WOOD} />
      {RACK_SPEARS.map((x) => (
        <group key={x} position={[x, 0, -0.015]} rotation={[-0.12, 0, 0]}>
          <Pole length={0.3} position={[0, 0.15, 0]} />
          <mesh geometry={G.spearTip} material={METAL} position={[0, 0.32, 0]} />
        </group>
      ))}
    </group>
  );
}

/** What the barracks is built of, by era. */
type Build = 'wood' | 'stone' | 'brick' | 'concrete';

/** The masonry of a stone (L3) or brick (L4) build; null for wood and concrete. */
function masonryOf(build: Build): THREE.Material | null {
  if (build === 'stone') return STONE;
  if (build === 'brick') return BRICK;
  return null;
}

/**
 * The hall: wooden with a team roof, or masonry (stone at L3, a brick drill
 * hall at L4, with a glazed ridge lantern and chimney stacks) with a second
 * storey under a hip roof.
 */
function Hall({ accent, masonry }: { accent: THREE.Material; masonry: THREE.Material | null }) {
  return (
    <group position={[0, 0, -0.18]}>
      <mesh geometry={G.hall} material={masonry ?? WOOD} position={[0, 0.1, 0]} />
      <mesh geometry={G.door} material={DARK_WOOD} position={[0, 0.06, 0.131]} />
      {masonry && (
        <>
          <mesh geometry={G.upperStorey} material={masonry} position={[0, 0.26, 0]} />
          {SHIELDS.map((x) => (
            <group key={x} position={[x, 0.26, 0.117]}>
              <mesh geometry={G.shield} material={accent} />
              <mesh geometry={G.shieldBoss} material={METAL} position={[0, 0, 0.008]} />
            </group>
          ))}
        </>
      )}
      {masonry === BRICK && (
        <>
          <mesh geometry={G.lantern} material={GLASS} position={[0, 0.48, 0]} />
          <mesh geometry={G.lanternRoof} material={SLATE} position={[0, 0.51, 0]} />
          {[-0.2, 0.2].map((x) => (
            <mesh key={x} geometry={G.stack} material={BRICK} position={[x, 0.42, -0.05]} />
          ))}
        </>
      )}
      {masonry ? (
        <mesh geometry={G.stoneHallRoof} material={accent} position={[0, 0.32, 0]} />
      ) : (
        <mesh geometry={G.hallRoof} material={accent} position={[0, 0.245, 0]} />
      )}
    </group>
  );
}

/** L5: a concrete hall under a flat team roof, banded by a glowing window strip. */
function ConcreteHall({ accent, glow }: { accent: THREE.Material; glow: THREE.Material }) {
  return (
    <group position={[0, 0, -0.18]}>
      <mesh geometry={G.concreteHall} material={CONCRETE} position={[0, 0.15, 0]} />
      <mesh geometry={G.door} material={DARK_CONCRETE} position={[0, 0.06, 0.141]} />
      <mesh geometry={G.hallStrip} material={glow} position={[0, 0.21, 0]} />
      <mesh geometry={G.flatRoof} material={accent} position={[0, 0.315, 0]} />
    </group>
  );
}

const WING_POS: [number, number, number] = [-0.34, 0, -0.36];
// The back wall: two runs 0.5 in from the tile's back edges, from the wing to
// the back corner and on to the watchtower, with a post at the corner.
const WALL_RUNS = [
  { x: -0.15, z: -0.49, length: 0.35, yaw: Math.PI / 6 },
  { x: 0.18, z: -0.473, length: 0.42, yaw: -Math.PI / 6 },
];
const WALL_MERLONS = [-0.36, -0.12, 0.12, 0.36];

/**
 * L3: a gatehouse wing with a team roof behind the hall, and the crenellated
 * back wall, in `material` (stone, then brick); `bunker` (L5, concrete) swaps
 * the pyramid roof for a flat one and drops the merlons.
 */
function Keep({
  accent,
  material,
  bunker = false,
}: {
  accent: THREE.Material;
  material: THREE.Material;
  bunker?: boolean;
}) {
  return (
    <>
      <group position={WING_POS}>
        <mesh geometry={G.wing} material={material} position={[0, 0.09, 0]} />
        <mesh geometry={G.door} material={DARK_WOOD} position={[0, 0.06, 0.101]} />
        {bunker ? (
          <mesh geometry={G.bunkerRoof} material={accent} position={[0, 0.195, 0]} />
        ) : (
          <mesh geometry={G.wingRoof} material={accent} position={[0, 0.23, 0]} />
        )}
      </group>
      {WALL_RUNS.map((w) => (
        <group key={w.x} position={[w.x, 0, w.z]} rotation={[0, w.yaw, 0]}>
          <mesh
            geometry={G.wall}
            material={material}
            position={[0, 0.05, 0]}
            scale={[w.length, 1, 1]}
          />
          {!bunker &&
            WALL_MERLONS.map((m) => (
              <mesh
                key={m}
                geometry={G.merlon}
                material={material}
                position={[m * w.length, 0.125, 0]}
              />
            ))}
        </group>
      ))}
      <mesh geometry={G.wallPost} material={material} position={[0, 0.07, -0.577]} />
    </>
  );
}

/**
 * Corner watchtower: open timber legs (wood) or a crenellated tower in
 * `masonry` (stone, then brick), with a team banner.
 */
function Watchtower({
  accent,
  masonry,
}: {
  accent: THREE.Material;
  masonry: THREE.Material | null;
}) {
  return (
    <group position={TOWER_POS}>
      {masonry ? (
        <>
          <mesh geometry={G.stoneTower} material={masonry} position={[0, 0.22, 0]} />
          {MERLONS.map(([x, z]) => (
            <mesh
              key={`${x}:${z}`}
              geometry={G.merlon}
              material={masonry}
              position={[x, 0.465, z]}
            />
          ))}
        </>
      ) : (
        <>
          {TOWER_LEGS.map(([x, z]) => (
            <Pole key={`${x}:${z}`} length={0.38} position={[x, 0.19, z]} />
          ))}
          <mesh geometry={G.towerDeck} material={DARK_WOOD} position={[0, 0.38, 0]} />
          <mesh geometry={G.towerRoof} material={accent} position={[0, 0.5, 0]} />
          {TOWER_LEGS.map(([x, z]) => (
            <Pole key={`p${x}:${z}`} length={0.08} position={[x, 0.43, z]} />
          ))}
        </>
      )}
      <mesh geometry={G.banner} material={accent} position={[0, 0.3, 0.088]} />
    </group>
  );
}

const RADAR_SPEED = 1.2; // radians per second
const RADAR_HEIGHT = 0.52;

/** L5, in the watchtower's corner: a steel mast on a concrete base with a turning radar dish. */
function RadarMast({ glow }: { glow: THREE.Material }) {
  return (
    <group position={TOWER_POS}>
      <mesh geometry={G.radarBase} material={CONCRETE} position={[0, 0.04, 0]} />
      <Pole length={RADAR_HEIGHT} position={[0, RADAR_HEIGHT / 2, 0]} material={STEEL} />
      <mesh geometry={G.radarLight} material={glow} position={[0, RADAR_HEIGHT * 0.6, 0.012]} />
      <Spin axis="y" speed={RADAR_SPEED} position={[0, RADAR_HEIGHT, 0]}>
        <mesh geometry={G.radarDish} material={STEEL} rotation={[-0.3, 0, 0]} />
      </Spin>
    </group>
  );
}

// Each palisade run as a straight wall: centre, length and turn.
const RUN_WALLS = PALISADE_RUNS.map(([[x0, z0], [x1, z1]]) => ({
  x: (x0 + x1) / 2,
  z: (z0 + z1) / 2,
  length: Math.hypot(x1 - x0, z1 - z0),
  yaw: Math.atan2(-(z1 - z0), x1 - x0),
}));
// Every other stake's spot carries a railing post.
const RAIL_POSTS = STAKES.filter((_, i) => i % 2 === 0);

/** The yard's enclosure along the palisade runs, by era: stakes, iron railings or a concrete wall. */
function Perimeter({ kind }: { kind: 'palisade' | 'rail' | 'wall' }) {
  if (kind === 'palisade') return <Palisade />;
  if (kind === 'wall') {
    return (
      <>
        {RUN_WALLS.map((w) => (
          <mesh
            key={w.x}
            geometry={G.perimeterWall}
            material={CONCRETE}
            position={[w.x, 0.065, w.z]}
            rotation={[0, w.yaw, 0]}
            scale={[w.length, 1, 1]}
          />
        ))}
      </>
    );
  }
  return (
    <>
      {RUN_WALLS.map((w) => (
        <group key={w.x} position={[w.x, 0, w.z]} rotation={[0, w.yaw, 0]}>
          {[0.05, 0.12].map((y) => (
            <mesh
              key={y}
              geometry={G.rail}
              material={IRON}
              position={[0, y, 0]}
              scale={[w.length, 1, 1]}
            />
          ))}
        </group>
      ))}
      {RAIL_POSTS.map(({ x, z }) => (
        <mesh key={`${x}:${z}`} geometry={G.railPost} material={IRON} position={[x, 0.07, z]} />
      ))}
    </>
  );
}

function Palisade() {
  return (
    <>
      {STAKES.map(({ x, z }) => (
        <group key={`${x}:${z}`} position={[x, 0, z]}>
          <mesh geometry={G.stake} material={WOOD} position={[0, 0.1, 0]} />
          <mesh geometry={G.stakeTip} material={WOOD} position={[0, 0.225, 0]} />
        </group>
      ))}
    </>
  );
}

/** A tall flagpole flying a team flag (slim, so it may pass the usual height cap). */
function Flagpole({
  accent,
  position,
}: {
  accent: THREE.Material;
  position: [number, number, number];
}) {
  return (
    <group position={position}>
      <Pole length={0.8} position={[0, 0.4, 0]} material={DARK_WOOD} />
      <mesh geometry={G.flag} material={accent} position={[0.09, 0.73, 0]} />
    </group>
  );
}

type BarracksLook = {
  /**
   * What hall, watchtower and keep are built of: wood; stone or brick (a
   * second storey and shields on the hall's front); concrete (flat-roofed
   * hall with a glowing window strip, a radar mast for the tower, a bunker).
   */
  build: Build;
  /**
   * A wider yard inside a palisade U, a corner watchtower with a team roof, an
   * armoury shed, an archery target and a spear rack.
   */
  outworks: boolean;
  /** Gatehouse wing and back wall, with a tall flagpole on the wing. */
  keep: boolean;
  /** What encloses the outworks' yard. */
  perimeter: 'palisade' | 'rail' | 'wall';
};

/**
 * Barracks look by level: entry i is level i + 1, levels past the end use the
 * last entry. Later eras add one entry each.
 */
const BARRACKS_LEVELS: readonly BarracksLook[] = [
  { build: 'wood', outworks: false, keep: false, perimeter: 'palisade' },
  { build: 'wood', outworks: true, keep: false, perimeter: 'palisade' },
  { build: 'stone', outworks: true, keep: true, perimeter: 'palisade' },
  { build: 'brick', outworks: true, keep: true, perimeter: 'rail' },
  { build: 'concrete', outworks: true, keep: true, perimeter: 'wall' },
];

/**
 * L1 a wooden hall with a team roof over a yard of dummies; L2 widens the yard
 * inside a palisade U, and adds a timber watchtower with a team roof and
 * banner, an armoury shed, an archery target and a spear rack; L3 rebuilds hall and tower in stone (hall two storeys under a hip
 * roof, shields on its front), adds a stone gatehouse wing flying a tall flag
 * and walls the back edge. L4 (Industrial) rebuilds it all in brick as a drill
 * hall behind iron railings; L5 (Modern) makes it a concrete compound: a
 * flat-roofed hall with a glowing window strip, a bunker, a perimeter wall and
 * a radar mast in place of the watchtower.
 */
export function Barracks({ accent, glow, level }: ModelProps) {
  const look = levelEntry(BARRACKS_LEVELS, level);
  const masonry = masonryOf(look.build);
  const concrete = look.build === 'concrete';
  return (
    <>
      {concrete ? (
        <ConcreteHall accent={accent} glow={glow} />
      ) : (
        <Hall accent={accent} masonry={masonry} />
      )}
      <mesh
        geometry={look.outworks ? G.bigYard : G.yard}
        material={SOIL}
        position={[0, 0.01, look.outworks ? 0.19 : 0.18]}
      />
      {DUMMIES.map((x) => (
        <TrainingDummy key={x} position={[x, 0, 0.2]} />
      ))}
      {look.outworks && (
        <>
          {concrete ? <RadarMast glow={glow} /> : <Watchtower accent={accent} masonry={masonry} />}
          <Perimeter kind={look.perimeter} />
          <WeaponShed accent={accent} position={ARMOURY} />
          <ArcheryTarget accent={accent} position={[-0.42, 0, 0.16]} />
          <SpearRack position={[0.36, 0, 0.3]} />
        </>
      )}
      {look.keep && (
        <>
          <Keep accent={accent} material={masonry ?? CONCRETE} bunker={concrete} />
          <Flagpole accent={accent} position={WING_POS} />
        </>
      )}
    </>
  );
}

const TILE_DUMMIES = [-0.3, -0.1, 0.1, 0.3];
// L2: a row of butts down the right side, leading to the target at the front.
const TILE_TARGETS: [number, number, number][] = [
  [0.47, 0, -0.22],
  [0.47, 0, 0.02],
  [0.28, 0, 0.36],
];
const WEAPON_SHED: [number, number, number] = [-0.48, 0, -0.05];

/** A weapon shed: team-coloured lean-to roof, shields on its +x front. */
function WeaponShed({
  accent,
  position,
}: {
  accent: THREE.Material;
  position: [number, number, number];
}) {
  return (
    <group position={position}>
      <mesh geometry={G.weaponShed} material={DARK_WOOD} position={[0, 0.06, 0]} />
      <mesh
        geometry={G.weaponShedRoof}
        material={accent}
        position={[0.01, 0.135, 0]}
        rotation={[0, 0, -0.3]}
      />
      {[-0.05, 0.05].map((z) => (
        <group key={z} position={[0.072, 0.07, z]} rotation={[0, Math.PI / 2, 0]}>
          <mesh geometry={G.shield} material={accent} />
          <mesh geometry={G.shieldBoss} material={METAL} position={[0, 0, 0.008]} />
        </group>
      ))}
    </group>
  );
}
const STALL_HORSES = [-0.17, 0.17];

/** A horse standing in its stall, facing +z. */
function Horse({ x }: { x: number }) {
  return (
    <group position={[x, 0, -0.11]}>
      <mesh geometry={G.horseBody} material={HORSE} position={[0, 0.14, 0]} />
      <mesh geometry={G.horseHead} material={HORSE} position={[0, 0.21, 0.12]} />
      {[-0.07, 0.07].map((z) =>
        [-0.025, 0.025].map((lx) => (
          <mesh
            key={`${lx}:${z}`}
            geometry={G.horseLeg}
            material={HORSE}
            position={[lx, 0.05, z]}
          />
        ))
      )}
    </group>
  );
}

/** A low lean-to stable across the back of the tile, two horses standing in front of it on straw. */
function Stable() {
  return (
    <>
      <mesh geometry={G.stallFloor} material={STRAW} position={[0, 0.036, -0.25]} />
      <mesh geometry={G.stableWall} material={DARK_WOOD} position={[0, 0.07, -0.44]} />
      <mesh geometry={G.stableDivider} material={WOOD} position={[0, 0.06, -0.35]} />
      <Pole length={0.1} position={[-0.33, 0.05, -0.28]} />
      <Pole length={0.1} position={[0.33, 0.05, -0.28]} />
      <mesh
        geometry={G.stableRoof}
        material={WOOD}
        position={[0, 0.12, -0.37]}
        rotation={[0.3, 0, 0]}
      />
      {STALL_HORSES.map((x) => (
        <Horse key={x} x={x} />
      ))}
    </>
  );
}

type YardLook = {
  /** The ground: soil, or a concrete pad (L5). */
  pad: THREE.Material;
  /** Archery targets down the right, a spear rack and a weapon shed on the left. */
  gear: boolean;
  /** Stable with horses at the back and a low stone sparring ring. */
  stable: boolean;
  /** A magazine at the back (brick, then concrete) with a firing line in front of it (sandbags, then concrete). */
  range: { magazine: THREE.Material; line: THREE.Material } | null;
  /** Team target boards in place of the dummies, and a floodlight. */
  modern: boolean;
};

/** Extra-tile look by level (tiles exist from L2); later eras add one entry each. */
const YARD_LEVELS: readonly YardLook[] = [
  { pad: SOIL, gear: false, stable: false, range: null, modern: false },
  { pad: SOIL, gear: true, stable: false, range: null, modern: false },
  { pad: SOIL, gear: true, stable: true, range: null, modern: false },
  {
    pad: SOIL,
    gear: true,
    stable: false,
    range: { magazine: BRICK, line: SANDBAG },
    modern: false,
  },
  {
    pad: CONCRETE,
    gear: false,
    stable: false,
    range: { magazine: CONCRETE, line: DARK_CONCRETE },
    modern: true,
  },
];

const MAGAZINE: [number, number, number] = [0, 0, -0.38];
const FIRING_LINE: [number, number, number] = [0, 0.03, -0.14];
const FLOODLIGHT: [number, number, number] = [0.46, 0, -0.3];

/**
 * A grown barracks' extra tile: a training yard of dummies; L2 adds a row of
 * archery targets, a spear rack and a weapon shed, L3 a stable with two horses
 * and a sparring ring. L4 swaps the stable for a brick magazine behind a
 * sandbag firing line; L5 paves it in concrete, with team target boards, a
 * concrete magazine and barrier, and a floodlight.
 */
export function BarracksTile({ accent, glow, level }: ModelProps) {
  const look = levelEntry(YARD_LEVELS, level);
  return (
    <>
      <HexPad material={look.pad} />
      {TILE_DUMMIES.map((x) =>
        look.modern ? (
          <group key={x} position={[x, 0.03, 0.1]}>
            <Pole length={0.1} position={[0, 0.05, 0]} material={STEEL} />
            <mesh geometry={G.board} material={accent} position={[0, 0.12, 0]} />
          </group>
        ) : (
          <TrainingDummy key={x} position={[x, 0, 0.1]} />
        )
      )}
      {look.range && (
        <>
          <group position={MAGAZINE}>
            <mesh geometry={G.magazine} material={look.range.magazine} position={[0, 0.07, 0]} />
            <mesh geometry={G.magazineRoof} material={accent} position={[0, 0.15, 0]} />
          </group>
          <mesh geometry={G.sandbags} material={look.range.line} position={FIRING_LINE} />
        </>
      )}
      {look.modern && (
        <group position={FLOODLIGHT}>
          <Pole length={0.46} position={[0, 0.23, 0]} material={STEEL} />
          <mesh geometry={G.floodlight} material={glow} position={[-0.02, 0.46, 0]} />
        </group>
      )}
      {look.gear && (
        <>
          {TILE_TARGETS.map((p) => (
            <ArcheryTarget key={p[2]} accent={accent} position={p} />
          ))}
          <SpearRack position={[-0.28, 0, 0.36]} />
          <WeaponShed accent={accent} position={WEAPON_SHED} />
        </>
      )}
      {look.stable && (
        <>
          <Stable />
          <mesh geometry={G.ring} material={STONE} position={[0, 0.02, 0.4]} />
        </>
      )}
    </>
  );
}
